import React, { useEffect, useState, useMemo, useCallback } from "react";
import moment from "moment";
import "./OpenHoursAdvanced.scss";

interface IWeekday {
  from?: string;
  to?: string;
}

interface IWeekdayData {
  MONDAY: IWeekday;
  TUESDAY: IWeekday;
  WEDNESDAY: IWeekday;
  THURSDAY: IWeekday;
  FRIDAY: IWeekday;
  SATURDAY: IWeekday;
  SUNDAY: IWeekday;
}

interface IOpenHour {
  from?: string;
  to?: string;
  status: "open" | "closed";
  description?: string;
  data?: IWeekdayData;
}

interface OpenHoursSimpleProps {
  name: string;
  openHours: IOpenHour[];
  testDate?: string;
}

export interface ILocation {
  id: string;
  name: string;
  type?: "ski_pass" | "ski_rental" | "restaurant" | "ski_schools";
  address?: string;
  description?: string;
  openHours: IOpenHour[];
  latitude?: number;
  longitude?: number;
  images?: string[];
}

const WEEKDAYS = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
] as const;
type WeekdayKey = (typeof WEEKDAYS)[number];

const ListOfHoursSimple = ({
  name,
  openHours,
  testDate,
}: OpenHoursSimpleProps) => {
  const [currentStatus, setCurrentStatus] = useState<string>("Zamknięte");
  const [statusClassName, setStatusClassName] = useState<string>(
    "berg-components-open-hours__list-close"
  );
  const [currentHours, setCurrentHours] = useState<string>("");

  const getCurrentDate = useCallback(
    () => (testDate ? moment(testDate) : moment()),
    [testDate]
  );

  const currentWeekdayIndex = useMemo(
    () => (getCurrentDate().day() + 6) % 7,
    [getCurrentDate]
  );

  const currentDay = WEEKDAYS[currentWeekdayIndex] as WeekdayKey;

  useEffect(() => {
    if (!openHours || openHours.length === 0) return;

    const currentOpenHour = openHours.find(
      (x) => x.from && x.to && getCurrentDate().isBetween(x.from, x.to)
    );

    if (!currentOpenHour || currentOpenHour.status === "closed") {
      setCurrentStatus("Zamknięte");
      setStatusClassName("berg-components-open-hours__list-close");
      setCurrentHours("");
      return;
    }

    if (currentOpenHour.data && currentDay in currentOpenHour.data) {
      const dayData = currentOpenHour.data[currentDay];

      if (dayData.from && dayData.to) {
        setCurrentHours(
          `${dayData.from.slice(0, 5)} - ${dayData.to.slice(0, 5)}`
        );

        const currentTime = getCurrentDate();
        const start = moment(dayData.from, "HH:mm:ss");
        const end = moment(dayData.to, "HH:mm:ss");
        const oneHourBeforeClosing = end.clone().subtract(1, "hours");

        if (currentTime.isBetween(start, oneHourBeforeClosing, null, "[]")) {
          setCurrentStatus("Otwarte");
          setStatusClassName("berg-components-open-hours__list-open");
        } else if (currentTime.isBetween(oneHourBeforeClosing, end)) {
          setCurrentStatus("Wkrótce zamknięte");
          setStatusClassName("berg-components-open-hours__list-soon");
        } else {
          setCurrentStatus("Zamknięte");
          setStatusClassName("berg-components-open-hours__list-close");
        }
      } else {
        setCurrentStatus("Zamknięte");
        setStatusClassName("berg-components-open-hours__list-close");
        setCurrentHours("");
      }
    }
  }, [openHours, currentDay, getCurrentDate]);

  if (!openHours || openHours.length === 0) {
    return null;
  }

  return (
    <div className="open-hours-simple">
      <div className="open-hours-card">
        <h2 className="location-name">{name}</h2>
        <div className="hours-info">
          <div className="status-container">
            <span className="status-label">Status:</span>
            <span className={`status-value ${statusClassName}`}>
              {currentStatus}
            </span>
          </div>
          <div className="hours-container">
            <span className="hours-value">{currentHours || "Zamknięte"}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListOfHoursSimple;
